import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { X, AlertCircle, Building } from 'lucide-react';
import { Client } from '../../../types/saas';
import saasApiService from '../../../services/saasApi';

const schema = yup.object().shape({
  company_name: yup.string().required('Nombre de la empresa requerido'),
  contact_name: yup.string().optional(),
  phone: yup
    .string()
    .optional()
    .test('telefono-peru', 'Teléfono inválido', (v) => {
      if (!v) return true;
      const digits = v.replace(/\D/g, '');
      // Permite fijo de 7-9 dígitos o móvil 9 dígitos; se formatea luego
      return digits.length >= 7 && digits.length <= 11;
    }),
  email: yup.string().email('Email inválido').optional(),
  address: yup.string().optional(),
  city: yup.string().optional(),
  state: yup.string().optional(),
  postal_code: yup.string().optional(),
  country: yup.string().optional(),
  tax_id: yup
    .string()
    .optional()
    .test('ruc-peru', 'RUC debe tener 11 dígitos', function (v) {
      const country = (this.parent?.country || '').toLowerCase();
      if (country.includes('per')) {
        const digits = (v || '').replace(/\D/g, '');
        return digits.length === 11;
      }
      return true;
    }),
  credit_limit: yup.number().min(0, 'Límite de crédito debe ser positivo').optional(),
  payment_terms: yup.number().min(0, 'Términos de pago deben ser positivos').optional(),
  status: yup.string().oneOf(['active', 'inactive', 'suspended']).required('Estado requerido'),
  industry: yup.string().optional(),
  website: yup.string().optional(),
  notes: yup.string().optional(),
});

interface ClientFormProps {
  client?: Client | null;
  onClose: () => void;
  onSave: () => void;
}

const ClientForm: React.FC<ClientFormProps> = ({ client, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [duplicateMatch, setDuplicateMatch] = useState<any | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      company_name: '',
      contact_name: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'Perú',
      tax_id: '',
      credit_limit: '',
      payment_terms: '',
      status: 'active',
      industry: '',
      website: '',
      notes: ''
    }
  });

  useEffect(() => {
    if (client) {
      reset({
        company_name: client.company_name,
        contact_name: client.contact_name || '',
        phone: client.phone || '',
        email: client.email || '',
        address: client.address || '',
        city: client.city || '',
        state: client.state || '',
        postal_code: client.postal_code || '',
        country: client.country || 'México',
        tax_id: client.tax_id || '',
        credit_limit: client.credit_limit ? client.credit_limit.toString() : '',
        payment_terms: client.payment_terms ? client.payment_terms.toString() : '',
        status: client.status,
        industry: client.industry || '',
        website: client.website || '',
        notes: client.notes || ''
      });
    }
  }, [client, reset]);

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setError('');

      // Convertir campos numéricos
      const formData = {
        ...data,
        // Normalizaciones
        company_name: String(data.company_name || '').trim(),
        email: data.email ? String(data.email).trim().toLowerCase() : '',
        country: data.country || 'Perú',
        credit_limit: data.credit_limit ? parseFloat(data.credit_limit) : null,
        payment_terms: data.payment_terms ? parseInt(data.payment_terms) : null,
      } as any;

      // Enviar también alias esperados por algunos backends
      // Mantiene compatibilidad sin romper el consumo actual
      formData.razon_social = formData.company_name;
      formData.ruc = data.tax_id || '';

      // Duplicados por RUC/Email
      setChecking(true);
      try {
        const rucNew = String(formData.ruc || formData.tax_id || '').replace(/\D/g, '');
        const emailNew = (formData.email || '').toLowerCase();
        let candidates: any[] = [];
        if (rucNew || emailNew) {
          candidates = await saasApiService.getClients?.({
            tax_id: rucNew,
            ruc: rucNew,
            email: emailNew,
            limit: 100,
          });
        }
        const match = (candidates || []).find((c: any) => {
          const ruc = String(c.ruc || c.tax_id || '').replace(/\D/g, '');
          const emailEq = (c.email || '').toLowerCase() === emailNew && emailNew !== '';
          const rucEq = rucNew !== '' && ruc === rucNew;
          return rucEq || emailEq;
        });
        if (!client && match) {
          const mName = match.company_name || match.razon_social || match.name || 'Cliente existente';
          const mRuc = String(match.ruc || match.tax_id || '');
          const mEmail = match.email || '';
          setDuplicateMatch(match);
          setError(`Ya existe un cliente con el mismo identificador. Nombre: ${mName}${mRuc ? `, RUC: ${mRuc}` : ''}${mEmail ? `, Email: ${mEmail}` : ''}.`);
          return;
        }
      } catch (_) { /* ignorar si el backend no soporta filtro */ }
      finally { setChecking(false); }

      if (client) {
        await saasApiService.updateClient(client.id, formData);
      } else {
        await saasApiService.createClient(formData);
      }

      onSave();
      onClose();
    } catch (error: any) {
      const base = error?.response?.data;
      const details = Array.isArray(base?.errors)
        ? base.errors.map((e: any) => e.msg || e.message || `${e.param || e.field}: inválido`).join('. ')
        : '';
      setError(details ? `${base?.message || 'Error al guardar el cliente'}. ${details}` : (base?.message || 'Error al guardar el cliente'));
    } finally {
      setLoading(false);
    }
  };

  // Helpers UI
  const formatPhonePe = (v: string) => {
    const digits = v.replace(/\D/g, '');
    // Formato +51 999 999 999 (si 9 dígitos)
    if (digits.length === 9) {
      return `+51 ${digits.slice(0,3)} ${digits.slice(3,6)} ${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('51')) {
      const d = digits.slice(2);
      return `+51 ${d.slice(0,3)} ${d.slice(3,6)} ${d.slice(6)}`;
    }
    return v; // dejar como está si otro caso
  };

  const fetchSunatByRuc = async (ruc: string) => {
    try {
      const clean = ruc.replace(/\D/g, '');
      if (clean.length !== 11) return null;
      // Permitir configurar una URL propia si existiera
      const base = (process.env.REACT_APP_SUNAT_RUC_API as string) || 'https://api.apis.net.pe/v1/ruc?numero=';
      const url = base.includes('?') ? `${base}${clean}` : `${base}${clean}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      const data = await res.json();
      // Normalizaciones según API apis.net.pe
      return {
        razon_social: data.razonSocial || data.nombre || data.razon_social || '',
        direccion: data.direccion || data.direccionFiscal || '',
        estado: data.estado || '',
        condicion: data.condicion || '',
        ubigeo: data.ubigeo || '',
        departamento: data.departamento || '',
        provincia: data.provincia || '',
        distrito: data.distrito || '',
        actividad: data.actividadEconomica || data.actividad || '',
      };
    } catch {
      return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {client ? 'Editar Cliente' : 'Agregar Cliente'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
                {duplicateMatch && (
                  <div className="mt-3 flex items-center space-x-3">
                    <button
                      type="button"
                      className="text-sm px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-100"
                      onClick={() => {
                        const ruc = String(duplicateMatch.ruc || duplicateMatch.tax_id || '');
                        const email = duplicateMatch.email || '';
                        const q = ruc || email;
                        // Navega a la lista con filtro de búsqueda
                        if (q) window.location.href = `/dashboard/clients?q=${encodeURIComponent(q)}`;
                        else window.location.href = `/dashboard/clients`;
                        onClose();
                      }}
                    >
                      Ver en lista
                    </button>
                    <button
                      type="button"
                      className="text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                      onClick={() => setDuplicateMatch(null)}
                    >
                      Ocultar
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Información básica */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
              <Building className="h-5 w-5 mr-2" />
              Información de la Empresa
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre de la Empresa *</label>
                <input
                  {...register('company_name')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Empresa ABC S.A.C."
                />
                {errors.company_name && <p className="mt-1 text-sm text-red-600">{errors.company_name.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Persona de Contacto</label>
                <input
                  {...register('contact_name')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Juan Pérez"
                />
                {errors.contact_name && <p className="mt-1 text-sm text-red-600">{errors.contact_name.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                <input
                  {...register('phone')}
                  type="tel"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+51 999 999 999"
                  onBlur={(e) => {
                    const formatted = formatPhonePe(e.target.value || '');
                    setValue('phone', formatted, { shouldValidate: true });
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  {...register('email')}
                  type="email"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="contacto@empresa.com"
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Dirección</label>
                <input
                  {...register('address')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Av. Principal 123"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Ciudad</label>
                <input
                  {...register('city')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Lima"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Estado/Provincia</label>
                <input
                  {...register('state')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Lima"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Código Postal</label>
                <input
                  {...register('postal_code')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="15001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">País</label>
                <input
                  {...register('country')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="México"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">RUC/NIT</label>
                <input
                  {...register('tax_id')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="20123456789"
                  onBlur={async (e) => {
                    const ruc = e.target.value || '';
                    const info = await fetchSunatByRuc(ruc);
                    if (info) {
                      if (info.razon_social) setValue('company_name', info.razon_social, { shouldValidate: true });
                      if (info.direccion) setValue('address', info.direccion);
                      if (info.departamento) setValue('state', info.departamento);
                      if (info.provincia || info.distrito) setValue('city', info.provincia ? `${info.provincia}${info.distrito ? ' - ' + info.distrito : ''}` : (info.distrito || ''));
                      if (info.actividad) setValue('industry', info.actividad);
                    }
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Estado *</label>
                <select
                  {...register('status')}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                  <option value="suspended">Suspendido</option>
                </select>
                {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>}
              </div>
            </div>
          </div>

          {/* Información comercial */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Información Comercial</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Límite de Crédito</label>
                <input
                  {...register('credit_limit')}
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                />
                {errors.credit_limit && <p className="mt-1 text-sm text-red-600">{errors.credit_limit.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Términos de Pago (días)</label>
                <input
                  {...register('payment_terms')}
                  type="number"
                  min="0"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="30"
                />
                {errors.payment_terms && <p className="mt-1 text-sm text-red-600">{errors.payment_terms.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Industria</label>
                <input
                  {...register('industry')}
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Transporte, Logística, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Sitio Web</label>
                <input
                  {...register('website')}
                  type="url"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://www.empresa.com"
                />
              </div>
            </div>
          </div>

          {/* Notas */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Notas</label>
            <textarea
              {...register('notes')}
              rows={4}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Información adicional sobre el cliente..."
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </div>
              ) : (
                client ? 'Actualizar' : 'Crear'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientForm;
